import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Holiday Deals', path: '/#stores', highlight: true },
    { name: 'How It Works', path: '/#how-it-works' },
    { name: 'Stores', path: '/#stores' },
  ];

  const handleNavClick = (path) => {
    if (path.includes('#')) {
      const elementId = path.split('#')[1];
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {/* Top Promotional Banner */}
      {showBanner && (
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white py-2.5 px-4 relative">
          <div className="container-custom flex items-center justify-center">
            <div className="flex items-center space-x-3 text-sm">
              <span className="hidden sm:inline">🎉</span>
              <span className="font-medium">
                TheCouponers — Your AI-powered savings assistant is coming soon!
              </span>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded transition-colors"
              aria-label="Close banner"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white shadow-soft'
            : 'bg-white'
        }`}
      >
        <div className="container-custom">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group flex-shrink-0">
              <div className="w-9 h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <svg
                  className="w-5 h-5 lg:w-6 lg:h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>
              <span className="text-lg lg:text-xl font-bold text-primary-500 hidden sm:block">
                The<span className="text-accent-500">Couponers</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-medium transition-colors hover:text-accent-500 flex items-center space-x-1 ${
                    link.highlight ? 'text-red-500' : 'text-gray-700'
                  }`}
                >
                  {link.highlight && <span>🎄</span>}
                  <span>{link.name}</span>
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* Sign In / Sign Up - Commented out for now */}
              {/* <div className="hidden md:flex items-center space-x-3 text-sm">
                <button className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                  Sign In
                </button>
                <button className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                  Sign Up
                </button>
              </div> */}
              
              <button
                disabled
                className="bg-gray-300 text-gray-500 font-semibold py-2 px-4 lg:py-2.5 lg:px-6 rounded-full text-sm cursor-not-allowed"
              >
                Coming Soon
              </button>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </nav>

          {/* Mobile Menu */}
          <div
            className={`lg:hidden overflow-hidden transition-all duration-300 ${
              isMobileMenuOpen ? 'max-h-96 pb-6' : 'max-h-0'
            }`}
          >
            <div className="flex flex-col space-y-4 pt-4 border-t border-gray-100">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-medium transition-colors hover:text-accent-500 ${
                    link.highlight ? 'text-red-500' : 'text-gray-700'
                  }`}
                >
                  {link.highlight && '🎄 '}{link.name}
                </Link>
              ))}
              {/* Sign In / Sign Up - Commented out for now */}
              {/* <div className="flex space-x-4 pt-2">
                <button className="text-gray-600 text-sm font-medium">Sign In</button>
                <button className="text-gray-600 text-sm font-medium">Sign Up</button>
              </div> */}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
